// MP-OS-197/203: the ONE place the UI names a missing article value. The wording comes from the
// shared display labels (displayLabels.json, identical to the API's copy), never from literals in
// components, so every list, chart, picker and report says the same thing.
import { displayLabel } from './displayLabels'
import { plural } from './format'
import { segmentFromApi } from './segments'

export const MISSING_LABEL_KEYS = {
  brand: '_brand_not_identified',
  name: '_name_missing',
  sku: '_article_number_missing',
  owner: '_not_assigned',
} as const

/** "Brand not identified": brand empty, whitespace or "(oresolvat)", and the brand list's remainder row. */
export const BRAND_NOT_IDENTIFIED = displayLabel(MISSING_LABEL_KEYS.brand)
/** "Name missing": the article has no name; shown after its SKU. */
export const NAME_MISSING = displayLabel(MISSING_LABEL_KEYS.name)
/** "Article number missing": the article has no SKU. */
export const ARTICLE_NUMBER_MISSING = displayLabel(MISSING_LABEL_KEYS.sku)
/** "Not assigned": the article has no owner (manufacturer). */
export const NOT_ASSIGNED = displayLabel(MISSING_LABEL_KEYS.owner)

// The API's name for the bucket of sales without a brand (dos-layer rankings, MPD-68).
const UNRESOLVED = '(oresolvat)'

export function isBrandMissing(brand: string | null | undefined): boolean {
  const name = brand?.trim()
  return !name || name === UNRESOLVED
}

export const brandLabel = (brand: string | null | undefined): string =>
  isBrandMissing(brand) ? BRAND_NOT_IDENTIFIED : brand!.trim()

export const isSkuMissing = (sku: string | null | undefined): boolean => !sku?.trim()

/** MP-OS-150: the suffix of a sold vape kit (device + pods, contents unknown), counted as one unit. */
export const KIT_SUFFIX = displayLabel('_kit_contents_unknown')
/** MPD-141 F-L1: the suffix of a kit whose contents are shown ("device + 3 pods"): only the volume
 *  is unknown, so "contents unknown" would contradict the contents line next to it. */
export const KIT_VOLUME_SUFFIX = displayLabel('_kit_volume_unknown')
/** The ⓘ text that explains the kit suffix. */
export const KIT_INFO = 'Kit contents are not reported, so the kit is counted as one unit.'

// MPD-135: a kit's contents from the API (`kit_contents`, next to `kit: true`; the Data MPD-134
// contract). `pods` null = pods per pack not stated (Vont, GEEK BAR), never invented. A part's
// `name` follows the article title (null → the "Name missing" rule). The part list's internal
// provenance is not sent (MPD-141). The volume of the contents stays unknown, so the kit is still one unit, labelled
// "kit (volume unknown)" (KIT_VOLUME_SUFFIX).
export type KitPart = { sku: string; name: string | null; count: number; role: 'device' | 'pod' | 'disposable'
  pods: number | null }
export type KitContents = { devices: number; pods: number | null; disposables: number; parts: KitPart[] }
/** The ⓘ text for kits whose contents are shown: what is known, and why it is still one unit. */
export const KIT_VOLUME_INFO = 'The volume of the contents is not reported, so the kit is counted as one unit.'

const count = (n: number | null | undefined): n is number => typeof n === 'number' && Number.isInteger(n) && n > 0

/** The ONE contents line of a kit: "device + 3 pods", "device + pod pack" (pod count unknown),
 *  "2 disposables". null for absent, null or empty contents. */
export function kitContentsText(contents: KitContents | null | undefined): string | null {
  if (!contents) return null
  const parts = [
    count(contents.devices) ? (contents.devices === 1 ? 'device' : `${contents.devices} devices`) : null,
    count(contents.pods) ? `${contents.pods} ${plural(contents.pods, 'pod')}`
      : contents.pods == null ? podPacks(contents.parts) : null,
    count(contents.disposables) ? `${contents.disposables} ${plural(contents.disposables, 'disposable')}` : null,
  ].filter(Boolean)
  return parts.length ? parts.join(' + ') : null
}

// The pod packs of unstated size: "pod pack", or "2 pod packs" when the kit holds more than one.
function podPacks(parts: KitPart[] | undefined): string | null {
  const packs = (parts ?? []).filter((p) => p.role === 'pod').reduce((n, p) => n + (count(p.count) ? p.count : 1), 0)
  return packs ? (packs === 1 ? 'pod pack' : `${packs} pod packs`) : null
}

/** The kit suffix: "kit (volume unknown)" when its contents line is shown, else "kit (contents unknown)". */
export const kitSuffix = (contents?: KitContents | null): string => kitContentsText(contents) ? KIT_VOLUME_SUFFIX : KIT_SUFFIX

/** A kit's parts, "7201 · VEEV ONE + 2 × 7215 · Name missing": each part's article title. */
export function kitPartsText(contents: KitContents | null | undefined): string | null {
  const parts = (contents?.parts ?? []).filter((p) => !isSkuMissing(p.sku))
  if (!parts.length) return null
  return parts.map((p) => `${count(p.count) && p.count > 1 ? `${p.count} × ` : ''}${articleTitle(p.sku, p.name)}`).join(' + ')
}

/** The kit row's ⓘ: its contents and parts when known, else today's KIT_INFO. */
export function kitInfo(contents: KitContents | null | undefined): string {
  const line = kitContentsText(contents)
  if (!line) return KIT_INFO
  const parts = kitPartsText(contents)
  return `Contents: ${line}${parts ? ` (${parts})` : ''}. ${KIT_VOLUME_INFO}`
}

// MPD-135 (MP-OS-178): a vape article's strength exactly as labelled (`strength_label`, the
// product data's strength string, e.g. "20mg"). Never converted, never read from a name: the
// verified mg/ml data is still pending. MPD-141 F-L2: the rule for which labels are shown lives in
// the API (strength_label is null for an mg/ml label); the web shows what the API gives.
export const STRENGTH_INFO = 'Strength as stated in the product data; not a verified mg/ml value.'

/** The strength tag, "20mg (as labelled)", or null: no label, or a non-vape row (when its
 *  segment is known). */
export function strengthText(label: string | null | undefined, segment?: string | null): string | null {
  const text = label?.trim()
  if (!text) return null
  if (segment != null && segmentFromApi(segment) !== 'VAPE') return null
  return `${text} (as labelled)`
}

// MP-OS-209: a verified pack variant from the API (Heart's title, or exactly one verified pack size
// since 2026-09-08; never the SKU suffix). It names same-name articles apart (LOOP 1706 / 1706-10).
export type PackVariant = { size: number; source: 'title' | 'pack_evidence' }

/** "10-pack" for a verified size, else null. The wording is the shared display label. */
export function packLabel(variant?: PackVariant | null): string | null {
  const size = variant?.size
  return typeof size === 'number' && Number.isInteger(size) && size >= 1
    ? displayLabel('_pack_variant').replace('{size}', String(size)) : null
}

const withPack = (title: string, variant?: PackVariant | null): string => {
  const pack = packLabel(variant)
  return pack ? `${title} · ${pack}` : title
}

// A real name: present and not just the SKU repeated.
const realName = (name: string | null | undefined, sku: string | null | undefined): string | null =>
  name?.trim() && name.trim() !== sku?.trim() ? name.trim() : null

/** A name's comparison key: trimmed, inner whitespace collapsed, case-insensitive (the API's name_key). */
export const nameKey = (name: string | null | undefined): string | null =>
  name?.trim() ? name.trim().split(/\s+/).join(' ').toLowerCase() : null

/** The name keys shared by two or more distinct SKUs in ONE list. A missing name is never
 *  ambiguous: its SKU is shown in its place. With pack variants, a name AND pack variant shared by
 *  two or more SKUs is ambiguous too (its key is namePackKey), so the variant cannot name them apart. */
export function ambiguousNames(rows: readonly { sku: string | null | undefined; name: string | null | undefined;
  packVariant?: PackVariant | null }[]): Set<string> {
  const skus = new Map<string, Set<string>>()
  const add = (key: string, sku: string | null | undefined) => skus.set(key, (skus.get(key) ?? new Set()).add(sku?.trim() ?? ''))
  for (const { sku, name, packVariant } of rows) {
    const key = nameKey(realName(name, sku))
    if (!key) continue
    add(key, sku)
    if (packLabel(packVariant)) add(namePackKey(key, packVariant), sku)
  }
  return new Set([...skus].filter(([, s]) => s.size > 1).map(([key]) => key))
}
// MPD-133 L12: the key of a name with its pack variant ("loop|10").
const namePackKey = (key: string, packVariant?: PackVariant | null): string => `${key}|${packVariant?.size}`

/** A kit's title: "<kit name> · kit (contents unknown)", or "· kit (volume unknown)" with known
 *  contents (kitSuffix); without a name, its SKU and "Name missing" lead. A verified pack variant
 *  comes before the kit suffix. */
export function kitLabel(name: string | null | undefined, sku?: string | null, packVariant?: PackVariant | null,
  kitContents?: KitContents | null): string {
  return `${withPack(realName(name, sku) ?? articleTitle(sku, null), packVariant)} · ${kitSuffix(kitContents)}`
}

/** An article's title: "<sku> · <name>", with the missing part named, then " · 10-pack" for a
 *  verified pack variant (MP-OS-209). A kit row (`kit: true`) gets the kit label. The SKU is shown,
 *  so a shared name needs nothing more. */
export function articleTitle(sku: string | null | undefined, name: string | null | undefined, kit?: boolean | null,
  packVariant?: PackVariant | null, kitContents?: KitContents | null): string {
  if (kit) return kitLabel(name, sku, packVariant, kitContents)
  const title = realName(name, sku)
  if (isSkuMissing(sku)) return withPack(title ? `${ARTICLE_NUMBER_MISSING} · ${title}` : ARTICLE_NUMBER_MISSING, packVariant)
  return withPack(`${sku!.trim()} · ${title ?? NAME_MISSING}`, packVariant)
}

/** MP-OS-209: an article's name where its SKU is NOT shown next to it (PDF top articles, chart
 *  legends). In this order:
 *  1. the name; without one, the article title ("<sku> · Name missing"), which shows the SKU;
 *  2. " (<sku>)" when the name is in `ambiguous` (shared by two or more SKUs in the same list, see
 *     ambiguousNames) and the SKU is not shown, unless a verified pack variant names it apart (the
 *     name and variant are not shared); then " · 10-pack" for the variant;
 *  3. the kit suffix for a kit (kitSuffix). */
export function articleName(sku: string | null | undefined, name: string | null | undefined,
  { kit, kitContents, packVariant, ambiguous }: { kit?: boolean | null; kitContents?: KitContents | null
    packVariant?: PackVariant | null; ambiguous?: ReadonlySet<string> } = {}): string {
  const title = realName(name, sku)
  if (!title) return articleTitle(sku, null, kit, packVariant, kitContents)
  const key = nameKey(title)!
  const shared = packLabel(packVariant) ? ambiguous?.has(namePackKey(key, packVariant)) : ambiguous?.has(key)
  const named = withPack(!isSkuMissing(sku) && shared ? `${title} (${sku!.trim()})` : title, packVariant)
  return kit ? `${named} · ${kitSuffix(kitContents)}` : named
}

/** MPD-10 item 9: the ONE neutral source name for every supplier-visible text (never "Heart"). */
export const DATA_SOURCE_NAME = displayLabel('_data_source')

export const ownerLabel = (owner: string | null | undefined): string => owner?.trim() || NOT_ASSIGNED
